import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import AdminImageUpload from '@/components/admin/AdminImageUpload';
import toast from 'react-hot-toast';
import type { BilingualText } from '@/types';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

const AdminNewsEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const newsArticles = useAdminStore((s) => s.newsArticles);
  const addNewsArticle = useAdminStore((s) => s.addNewsArticle);
  const updateNewsArticle = useAdminStore((s) => s.updateNewsArticle);
  const fieldLimits = useAdminStore((s) => s.settings?.fieldLimits);

  const isEditing = id !== undefined && id !== 'new';
  const existing = isEditing ? newsArticles.find((n) => n._id === id) : undefined;

  const [title, setTitle] = useState<BilingualText>({ en: '', ka: '' });
  const [excerpt, setExcerpt] = useState<BilingualText>({ en: '', ka: '' });
  const [content, setContent] = useState<BilingualText>({ en: '', ka: '' });
  const [coverImage, setCoverImage] = useState('');
  const [publishedAt, setPublishedAt] = useState(new Date().toISOString().slice(0, 10));
  const [isPublished, setIsPublished] = useState(false);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setExcerpt(existing.excerpt);
      setContent(existing.content);
      setCoverImage(existing.coverImage);
      setPublishedAt(existing.publishedAt);
      setIsPublished(existing.isPublished);
    }
  }, [existing]);

  const handleSave = (): void => {
    try {
      if (!title.en.trim()) {
        toast.error('Title (EN) is required');
        return;
      }
      if (!content.en.trim()) {
        toast.error('Content (EN) is required');
        return;
      }

      if (isEditing && existing) {
        updateNewsArticle(existing._id, { title, excerpt, content, coverImage, publishedAt, isPublished });
        toast.success('Article updated successfully!');
      } else {
        addNewsArticle({ title, excerpt, content, coverImage, publishedAt, isPublished });
        toast.success('Article created successfully!');
      }
      navigate('/admin/news');
    } catch (err) {
      console.error('Failed to save article:', err);
      toast.error('Failed to save article. Please try again.');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <Link to="/admin/news" className="w-9 h-9 rounded-xl border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:border-black/15 dark:hover:border-white/15 transition-all">
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">
            {isEditing ? 'Edit Article' : 'New Article'}
          </h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {isEditing ? existing?.title.en : 'Write a new press article'}
          </p>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-5">
        <AdminBilingualInput label="Title" value={title} onChange={setTitle} placeholder="Article title" />
        <AdminBilingualInput label="Excerpt" value={excerpt} onChange={setExcerpt} textarea placeholder="Short preview text" />
        <AdminBilingualInput label="Content" value={content} onChange={setContent} textarea placeholder="Full article content — paste links directly (https://...) to make them clickable" rows={12} />
        <AdminImageUpload label="Cover Image" value={coverImage} onChange={setCoverImage} shape="rect" />
        <div>
          <label className={LABEL_CLS}>Published Date</label>
          <input type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className={INPUT_CLS} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <label className={LABEL_CLS}>Published</label>
            <p className="text-[10px] text-black/30 dark:text-white/30">Make this article visible on the news page</p>
          </div>
          <button onClick={() => setIsPublished(!isPublished)} className={`w-10 h-5 rounded-full transition-all ${isPublished ? 'bg-black dark:bg-white' : 'bg-black/10 dark:bg-white/10'}`}>
            <span className={`block w-4 h-4 rounded-full bg-white dark:bg-black transition-transform ${isPublished ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </motion.div>
      <div className="flex items-center gap-3">
        <button onClick={handleSave} className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          <Save size={14} /> {isEditing ? 'Save Changes' : 'Create Article'}
        </button>
        <Link to="/admin/news" className="px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors">Cancel</Link>
      </div>
    </div>
  );
};

export default AdminNewsEditorPage;
